import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  Button,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Alert,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  LinearProgress
} from '@mui/material';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  LineChart,
  Line,
  PieChart,
  Pie,
  Cell,
  ResponsiveContainer
} from 'recharts';
import { WhatsApp, TrendingUp, Assessment, People, Timeline } from '@mui/icons-material';

const WhatsAppMonitoring = () => {
  const [dashboardData, setDashboardData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedPeriod, setSelectedPeriod] = useState(30);
  const [refreshing, setRefreshing] = useState(false);

  const fetchDashboardData = async (days = 30) => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      
      const response = await fetch(`/api/whatsapp/adoption/dashboard?days=${days}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error(`Erreur ${response.status}: ${response.statusText}`);
      }

      const data = await response.json();
      setDashboardData(data);
      setError(null);
    } catch (err) {
      console.error('Erreur récupération données dashboard:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleFlushMetrics = async () => {
    try {
      setRefreshing(true);
      const token = localStorage.getItem('token');
      
      const response = await fetch('/api/whatsapp/adoption/flush', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error(`Erreur ${response.status}: ${response.statusText}`);
      }

      // Rafraîchir les données après le flush
      await fetchDashboardData(selectedPeriod);
    } catch (err) {
      console.error('Erreur flush métriques:', err);
      setError(err.message);
    } finally {
      setRefreshing(false);
    }
  };

  useEffect(() => {
    fetchDashboardData(selectedPeriod);
  }, [selectedPeriod]);

  const handlePeriodChange = (event) => {
    setSelectedPeriod(event.target.value);
  };

  const formatPercentage = (value) => {
    return `${value?.toFixed(1) || 0}%`;
  };

  const formatNumber = (value) => {
    return value?.toLocaleString() || 0;
  };

  const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884D8'];

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
        <Typography variant="h6" sx={{ ml: 2 }}>
          Chargement des métriques WhatsApp...
        </Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Box p={3}>
        <Alert severity="error" sx={{ mb: 2 }}>
          Erreur lors du chargement des données: {error}
        </Alert>
        <Button variant="contained" onClick={() => fetchDashboardData(selectedPeriod)}>
          Réessayer
        </Button>
      </Box>
    );
  }

  const overview = dashboardData?.overview || {};
  const recentTrend = dashboardData?.recent_trend || {};
  const dailyBreakdown = dashboardData?.daily_breakdown || [];
  const topUsers = dashboardData?.top_users || [];
  const metricsSummary = dashboardData?.metrics_summary || {};

  // Préparer les données pour les graphiques
  const dailyChartData = dailyBreakdown.map(day => ({
    date: new Date(day.date).toLocaleDateString('fr-FR', { month: 'short', day: 'numeric' }),
    queries: day.queries,
    whatsapp_found: day.whatsapp_found,
    whatsapp_used: day.whatsapp_used,
    adoption_rate: day.queries > 0 ? (day.queries_with_whatsapp / day.queries * 100) : 0
  }));

  const adoptionPieData = [
    { name: 'Avec WhatsApp', value: metricsSummary.queries_with_whatsapp, color: '#00C49F' },
    { name: 'Sans WhatsApp', value: overview.total_queries - metricsSummary.queries_with_whatsapp, color: '#FF8042' }
  ];

  const usagePieData = [
    { name: 'WhatsApp Utilisé', value: metricsSummary.queries_using_whatsapp, color: '#0088FE' },
    { name: 'WhatsApp Non Utilisé', value: metricsSummary.queries_with_whatsapp - metricsSummary.queries_using_whatsapp, color: '#FFBB28' }
  ];

  return (
    <Box p={3}>
      {/* En-tête */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Box display="flex" alignItems="center">
          <WhatsApp sx={{ fontSize: 40, color: '#25D366', mr: 2 }} />
          <Box>
            <Typography variant="h4" component="h1">
              Monitoring WhatsApp
            </Typography>
            <Typography variant="subtitle1" color="text.secondary">
              Suivi de l'adoption des messages WhatsApp dans les réponses du chatbot
            </Typography>
          </Box>
        </Box>
        
        <Box display="flex" gap={2} alignItems="center">
          <FormControl size="small" sx={{ minWidth: 120 }}>
            <InputLabel>Période</InputLabel>
            <Select
              value={selectedPeriod}
              label="Période"
              onChange={handlePeriodChange}
            >
              <MenuItem value={7}>7 jours</MenuItem>
              <MenuItem value={30}>30 jours</MenuItem>
              <MenuItem value={90}>90 jours</MenuItem>
            </Select>
          </FormControl>
          
          <Button
            variant="outlined"
            onClick={handleFlushMetrics}
            disabled={refreshing}
            startIcon={refreshing ? <CircularProgress size={16} /> : <Assessment />}
          >
            {refreshing ? 'Actualisation...' : 'Actualiser'}
          </Button>
          
          <Button
            variant="contained"
            onClick={() => fetchDashboardData(selectedPeriod)}
            startIcon={<Timeline />}
          >
            Rafraîchir
          </Button>
        </Box>
      </Box>

      {/* Métriques principales */}
      <Grid container spacing={3} mb={4}>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center" justifyContent="space-between">
                <Box>
                  <Typography color="text.secondary" gutterBottom>
                    Requêtes Totales
                  </Typography>
                  <Typography variant="h4">
                    {formatNumber(overview.total_queries)}
                  </Typography>
                </Box>
                <Assessment sx={{ fontSize: 40, color: 'primary.main' }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center" justifyContent="space-between">
                <Box>
                  <Typography color="text.secondary" gutterBottom>
                    Taux d'Adoption
                  </Typography>
                  <Typography variant="h4" color="success.main">
                    {formatPercentage(overview.whatsapp_adoption_rate)}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Tendance 7j: {formatPercentage(recentTrend.adoption_rate)}
                  </Typography>
                </Box>
                <TrendingUp sx={{ fontSize: 40, color: 'success.main' }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center" justifyContent="space-between">
                <Box>
                  <Typography color="text.secondary" gutterBottom>
                    Taux d'Utilisation
                  </Typography>
                  <Typography variant="h4" color="info.main">
                    {formatPercentage(overview.whatsapp_usage_rate)}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Tendance 7j: {formatPercentage(recentTrend.usage_rate)}
                  </Typography>
                </Box>
                <WhatsApp sx={{ fontSize: 40, color: '#25D366' }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center" justifyContent="space-between">
                <Box>
                  <Typography color="text.secondary" gutterBottom>
                    Temps Moyen
                  </Typography>
                  <Typography variant="h4">
                    {overview.avg_processing_time?.toFixed(2) || 0}s
                  </Typography>
                </Box>
                <Timeline sx={{ fontSize: 40, color: 'warning.main' }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Graphiques */}
      <Grid container spacing={3} mb={4}>
        {/* Évolution quotidienne */}
        <Grid item xs={12} lg={8}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Évolution Quotidienne
              </Typography>
              <ResponsiveContainer width="100%" height={300}>
                <LineChart data={dailyChartData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="date" />
                  <YAxis yAxisId="left" />
                  <YAxis yAxisId="right" orientation="right" />
                  <Tooltip />
                  <Legend />
                  <Bar yAxisId="left" dataKey="queries" fill="#8884d8" name="Requêtes" />
                  <Line yAxisId="right" type="monotone" dataKey="adoption_rate" stroke="#ff7300" name="Taux d'adoption (%)" />
                </LineChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        </Grid>

        {/* Répartition adoption */}
        <Grid item xs={12} lg={4}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Répartition Adoption
              </Typography>
              <ResponsiveContainer width="100%" height={300}>
                <PieChart>
                  <Pie
                    data={adoptionPieData}
                    cx="50%"
                    cy="50%"
                    labelLine={false}
                    label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                    outerRadius={80}
                    fill="#8884d8"
                    dataKey="value"
                  >
                    {adoptionPieData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.color} />
                    ))}
                  </Pie>
                  <Tooltip />
                </PieChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Détails des métriques */}
      <Grid container spacing={3} mb={4}>
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Métriques Détaillées
              </Typography>
              <Box display="flex" flexDirection="column" gap={2}>
                <Box>
                  <Typography variant="body2" color="text.secondary">
                    Messages WhatsApp Trouvés
                  </Typography>
                  <Typography variant="h6">
                    {formatNumber(metricsSummary.total_whatsapp_found)}
                  </Typography>
                  <LinearProgress 
                    variant="determinate" 
                    value={metricsSummary.total_whatsapp_found > 0 ? 100 : 0} 
                    sx={{ mt: 1 }}
                  />
                </Box>
                
                <Box>
                  <Typography variant="body2" color="text.secondary">
                    Messages WhatsApp Utilisés
                  </Typography>
                  <Typography variant="h6">
                    {formatNumber(metricsSummary.total_whatsapp_used)}
                  </Typography>
                  <LinearProgress 
                    variant="determinate" 
                    value={metricsSummary.total_whatsapp_found > 0 ? 
                      (metricsSummary.total_whatsapp_used / metricsSummary.total_whatsapp_found * 100) : 0
                    } 
                    sx={{ mt: 1 }}
                  />
                </Box>
                
                <Box>
                  <Typography variant="body2" color="text.secondary">
                    Requêtes avec WhatsApp
                  </Typography>
                  <Typography variant="h6">
                    {formatNumber(metricsSummary.queries_with_whatsapp)}
                  </Typography>
                  <LinearProgress 
                    variant="determinate" 
                    value={overview.total_queries > 0 ? 
                      (metricsSummary.queries_with_whatsapp / overview.total_queries * 100) : 0
                    } 
                    sx={{ mt: 1 }}
                  />
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* Top utilisateurs */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Top Utilisateurs
              </Typography>
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Utilisateur</TableCell>
                      <TableCell align="right">Requêtes</TableCell>
                      <TableCell align="right">WhatsApp</TableCell>
                      <TableCell align="right">Taux</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {topUsers.slice(0, 10).map((user, index) => (
                      <TableRow key={user.user_id || index}>
                        <TableCell>
                          <Chip 
                            label={user.user_id || 'Anonyme'} 
                            size="small" 
                            color="primary" 
                            variant="outlined"
                          />
                        </TableCell>
                        <TableCell align="right">{formatNumber(user.queries)}</TableCell>
                        <TableCell align="right">{formatNumber(user.whatsapp_used)}</TableCell>
                        <TableCell align="right">
                          <Chip 
                            label={formatPercentage(user.queries > 0 ? (user.queries_with_whatsapp / user.queries * 100) : 0)}
                            size="small"
                            color={user.queries_with_whatsapp > 0 ? 'success' : 'default'}
                          />
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Informations système */}
      <Card>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Informations Système
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6} md={3}>
              <Typography variant="body2" color="text.secondary">
                Période d'analyse
              </Typography>
              <Typography variant="body1">
                {selectedPeriod} jours
              </Typography>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Typography variant="body2" color="text.secondary">
                Dernière mise à jour
              </Typography>
              <Typography variant="body1">
                {new Date().toLocaleString('fr-FR')}
              </Typography>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Typography variant="body2" color="text.secondary">
                Efficacité WhatsApp
              </Typography>
              <Typography variant="body1">
                {metricsSummary.total_whatsapp_found > 0 ? 
                  formatPercentage(metricsSummary.total_whatsapp_used / metricsSummary.total_whatsapp_found * 100) : 
                  '0%'
                }
              </Typography>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Typography variant="body2" color="text.secondary">
                Statut monitoring
              </Typography>
              <Chip label="Actif" color="success" size="small" />
            </Grid>
          </Grid>
        </CardContent>
      </Card>
    </Box>
  );
};

export default WhatsAppMonitoring;